import { ExprException } from "./exception"

const str = (matcher: string, skip = false) => ({ matcher, skip })
const regExp = (matcher: RegExp, skip = false) => ({ matcher, skip })
const keyword = (keyword: string) => ({ keyword, skip: false })

const yamLexerConfig = {
  'OpenTag': str('~{'),
  'ObjectOpenTag': str('{'),
  'CloseTag': str('}'),
  'Bar': str('|'),
  'ArrayOpenTag': str('['),
  'ArrayCloseTag': str(']'),
  'Colon': str(':'),
  'Case': keyword('case'),
  'NullLiteral': keyword('null'),
  'Default': keyword('default'),
  'Or': keyword('or'),
  'Spread': str('...'),
  'Add': str('+'),
  'NumberLiteral': regExp(/^[+-]?[0-9]+(?:\.[0-9]+)?/),
  'StringLiteral': regExp(/^((?<!\\)["'].{0,512}?(?<!\\)["'])/),
  'BoolLiteral': regExp(/^true|^false/),
  'Identifier': regExp(/^[a-zA-Z_][a-zA-Z0-9._-]*/),
  'Comma': str(','),
  'Whitespace': regExp(/^[ \t\n\f]+/, true)
}

type YamLexerConfig = typeof yamLexerConfig
export type TokenKind = keyof YamLexerConfig

export type Lexer = {
  position: number
  next(): { done: boolean, value: Token | null }
  // Returns a new lexer with current position
  clone(): Lexer
}

// iter converts lexer to Iterable
export function iter(lexer: Lexer): Iterable<Token | null> {
  return {
    [Symbol.iterator]: () => ({
      next: () => lexer.next()
    })
  }
}

/**
 * Example:
 *  const it = iter(lexer(yamLexerConfig)('~{asd, '123'}'))
 *  [...it] == [
 *    { kind: 'OpenTag', value: '~{', position: [0, 2] },
 *    { kind: 'Identifier', value: 'asd', position: [2, 5] },
 *    { kind: 'Comma', value: ',', position: [5, 6] },
 *    { kind: 'StringLiteral', value: "'123'", position: [7, 12] },
 *    { kind: 'CloseTag', value: '}', position: [12, 13] }
 *  ]
 */
function lexer(lexerConfig: YamLexerConfig): (content: string) => Lexer {
  type LexerConfigItemWithKind = LexerConfigItem & { kind: TokenKind }

  const lexerConfigArr = Object
    .keys(lexerConfig)
    .filter((k: TokenKind) => 'matcher' in lexerConfig[k])
    .map((k: TokenKind) => ({ ...lexerConfig[k], kind: k } as LexerConfigItemWithKind))

  const keywords = Object
    .keys(lexerConfig)
    .filter((k: TokenKind) => 'keyword' in lexerConfig[k])
    .map((k: TokenKind) => ({ keyword: (lexerConfig[k] as { keyword: string }).keyword, kind: k }))
    .reduce((prev, cur) => {
      prev[cur.keyword] = cur.kind
      return prev
    }, {} as Record<string, TokenKind>)
  const allKeywords = new Set(Object.keys(keywords))
  // match('~{}') => { kind: 'OpenTag', value: '~{' }
  function match(input: string): { kind: TokenKind, value: string } | null {
    for (const lc of lexerConfigArr) {
      let token: { kind: TokenKind, value: string } | null = null
      if (lc.matcher instanceof RegExp) {
        const matches = input.match(lc.matcher)
        if (matches) {
          token = { kind: lc.kind, value: matches[0] }
        }
      } else {
        if (input.startsWith(lc.matcher)) {
          token = { kind: lc.kind, value: lc.matcher }
        }
      }
      if (token) {
        if (token.kind === 'Identifier') {
          if (allKeywords.has(token.value)) {
            token = { kind: keywords[token.value], value: token.value }
          }
        }
        return token
      }
    }
    return null
  }

  return content => {
    function matchToken(position: number): ReturnType<Lexer['next']> & { position: number } {
      if (position == content.length) {
        return { value: null, done: true, position }
      }
      let matchRes
      do {
        matchRes = match(content.substring(position))
        if (!matchRes) {
          throw new ExprException([position, position + 1], 'SyntaxError: invalid syntax.')
        }
        position += matchRes.value.length
      } while (lexerConfig[matchRes.kind].skip)

      const { kind, value } = matchRes
      return {
        value: {
          kind,
          value,
          codePos: [position - value.length, position],
        },
        done: false,
        position
      }
    }

    function makeLexer(position: number): Lexer {
      return {
        position,
        next() {
          const t = matchToken(this.position)
          this.position = t.position
          return t
        },
        clone() {
          return makeLexer(this.position)
        }
      }
    }
    return makeLexer(0)
  }
}


export type Token = {
  kind: TokenKind,
  value: string,
  codePos: [number, number]
}

type LexerConfigItem = {
  matcher: string | RegExp,
  skip: boolean
}

export default lexer(yamLexerConfig)
